/**
 * Result of an authentication attempt (#26615).
 *
 * `authenticate()` answers with a boolean, which merges four very different situations into one:
 * "no credentials at all", "credentials exist but the backend is unreachable right now", "the
 * local account cache is missing" and "the token was rejected". Only the first and the last mean
 * the user is logged out — but a boolean cannot tell them apart, neither in the code nor in Sentry.
 *
 * This outcome restores the distinction. It changes no behaviour by itself: every outcome except
 * `Authenticated`/`OfflineAuthenticated` still ends on the login page today. What it adds is the
 * *reason*, reported by the sync pipeline, so the share of login redirects that are not real
 * logouts becomes measurable before that behaviour is changed.
 */
export enum AuthOutcome {
  /** Verified against the backend, account is fresh. */
  Authenticated = 'Authenticated',
  /** No backend, but a valid account is available from memory or the cache. */
  OfflineAuthenticated = 'OfflineAuthenticated',
  /** A token exists, but there is no usable account — the next online run has to fetch it. */
  NeedsOnlineReauth = 'NeedsOnlineReauth',
  /** Network, timeout or server error. The session is still valid, this is not a logout. */
  Unavailable = 'Unavailable',
  /** No token, or the token was rejected. This is the only real logout. */
  Unauthenticated = 'Unauthenticated',
}

/** True when the pipeline may continue: an account is available. */
export function isAuthenticated(outcome: AuthOutcome): boolean {
  return outcome === AuthOutcome.Authenticated || outcome === AuthOutcome.OfflineAuthenticated;
}

/**
 * True when the outcome does not mean the user is logged out. `Unauthenticated` is the only
 * outcome that would justify ending the session.
 */
export function keepsSession(outcome: AuthOutcome): boolean {
  return outcome !== AuthOutcome.Unauthenticated;
}
